import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  DestroyRef,
  effect,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  Injector,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { productAttributesTranslations } from '@epicuro-next/constants/features/localization';
import { mapDescriptionAttrsToPanelFields } from '@epicuro-next/dynamic-elements/utilities';
import { selectAttributesPriorityConfig } from '@epicuro-next/platform/configuration';
import {
  selectExpandButtonPeekRule,
  selectExpandingCardAttributes,
  selectExpandingProductCardFooterButtonAttributes,
  selectFiltersVisibleSettingValue,
  selectHorizontalViewEnabledSettingValue,
} from '@epicuro-next/state/p-basket-state';
import { selectAsideMenuOpened } from '@epicuro-next/state/user-ui-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { select, Store } from '@ngrx/store';
import { SuggestService } from 'data-recommendation';
import { SimilarProductsStore } from 'features-similar-products';
import { Product } from 'graphql-api/gql-api-interfaces';
import {
  combineLatest,
  debounceTime,
  fromEvent,
  Subscription,
  tap,
} from 'rxjs';

import { ProductExpandDetailsContentType } from '../../constants/product-details-content-type';
import { ProductCardComponent } from '../../product-card/product-card.component';
import { mapAndSortDescriptionAttributes } from './utils/map-and-sort-description-attributes';

const expandWrapperLeftCssProperty = '--expand-wrapper-left';
const reverseExpandClassName = 'reverse-expand';

@Component({
  selector: 'epc-product-card-expand-container',
  templateUrl: './product-card-expand-container.component.html',
  styleUrls: ['./product-card-expand-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [SimilarProductsStore, SuggestService],
})
export class ProductCardExpandContainerComponent
  implements AfterViewInit, OnInit
{
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly hostEl = inject(ElementRef<HTMLElement>).nativeElement;
  private injector = inject(Injector);

  public readonly keepOpen = output();

  public readonly expandOnHover = input(false);

  public readonly productCard = contentChild(ProductCardComponent);

  public readonly isHovered = signal(false);
  public readonly isKeepOpen = signal(false);

  public isPeekIconRule = toSignal(
    this.store.select(selectExpandButtonPeekRule),
  );

  public readonly isPeekIcon = computed(() => {
    return this.isPeekIconRule() && Math.random() < 0.5;
  });

  public readonly isExpanded = computed(() => {
    return this.isHovered() || this.isKeepOpen();
  });

  public readonly isReverse = signal(false);

  public readonly currentVariant = computed(() => {
    return this.productCard()?.currentVariant();
  });

  public readonly productSignal = computed(() => {
    return this.productCard()?.productSignal();
  });

  private readonly selectedProductCardContent =
    signal<ProductExpandDetailsContentType | null>(null);

  public readonly shownProductType = computed(
    () =>
      this.selectedProductCardContent() ??
      ProductExpandDetailsContentType.Information,
  );

  public readonly ProductExpandDetailsContentType =
    ProductExpandDetailsContentType;

  private readonly expandingProductCardFooterButtonAttributes = toSignal(
    this.store.pipe(select(selectExpandingProductCardFooterButtonAttributes)),
  );

  private readonly expandingCardAttributes = toSignal(
    this.store.pipe(select(selectExpandingCardAttributes)),
    { initialValue: [] },
  );

  public readonly showFooterButton = computed(() => {
    const attributes = this.expandingProductCardFooterButtonAttributes();
    return isNonNullish(attributes) && attributes?.length > 0;
  });

  public readonly attributesPriorityConfig = toSignal(
    this.store.pipe(select(selectAttributesPriorityConfig)),
  );

  public selectedVariantWithDescriptionAttributesFallback = computed(() => {
    const selectedVariant = this.currentVariant();
    const productAttributes = this.productSignal()?.attributes ?? {};
    const expandingAttributes = this.expandingCardAttributes() ?? [];
    const descriptionAttributes = selectedVariant?.descriptionAttributes?.length
      ? selectedVariant?.descriptionAttributes
      : this.productSignal()?.descriptionAttributes;

    return {
      ...selectedVariant,
      descriptionAttributes: mapAndSortDescriptionAttributes(
        descriptionAttributes,
        expandingAttributes,
        productAttributesTranslations,
      ),
      attributes: {
        ...Object.keys(productAttributes)
          .filter((key) =>
            descriptionAttributes?.some((attr) => attr.key === key),
          )
          .reduce(
            (acc, key) => ({ ...acc, [key]: productAttributes[key] }),
            {},
          ),
        ...(selectedVariant?.attributes ?? {}),
      },
    };
  });

  public panelDef = computed(() => {
    const selectedVariantWithDescriptionAttributesFallback =
      this.selectedVariantWithDescriptionAttributesFallback();
    const attributesPriorityConfig = this.attributesPriorityConfig();
    return mapDescriptionAttrsToPanelFields(
      selectedVariantWithDescriptionAttributesFallback as unknown as Product,
      attributesPriorityConfig,
    );
  });

  public showSimilarProducts$?: Subscription;

  private animationTimeout: ReturnType<typeof setTimeout>;

  @HostBinding('class.is-expanded') public get isExpandedClass() {
    return this.isExpanded();
  }

  @HostBinding(`class.${reverseExpandClassName}`) public get isReverseClass() {
    return this.isReverse();
  }

  @HostListener('mouseenter') public onMouseEnter() {
    if (this.expandOnHover()) {
      this.isHovered.set(true);
    }
  }

  @HostListener('mouseleave') public onMouseLeave() {
    this.isHovered.set(false);
  }

  constructor() {
    effect(() => {
      if (this.isExpanded()) {
        clearTimeout(this.animationTimeout);
        this.hostEl.style.setProperty('--product-card-expand-z-index', '105');
      } else {
        this.hostEl.style.setProperty('--product-card-expand-z-index', '103');
        this.productCard()?.showSimilarProducts.set(false);
        this.selectedProductCardContent.set(
          ProductExpandDetailsContentType.Information,
        );
        this.animationTimeout = setTimeout(() => {
          this.hostEl.style.setProperty(
            '--product-card-expand-z-index',
            'auto',
          );
        }, 900);
      }
    });
  }

  public ngOnInit() {
    combineLatest([
      this.store.select(selectAsideMenuOpened),
      this.store.select(selectFiltersVisibleSettingValue),
      this.store.select(selectHorizontalViewEnabledSettingValue),
    ])
      .pipe(
        debounceTime(300),
        tap(() => {
          this.setExpandAnimationVariables();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public ngAfterContentInit() {
    this.showSimilarProducts$ = this.productCard()
      ? toObservable(
          (this.productCard() as ProductCardComponent).showSimilarProducts,
          {
            injector: this.injector,
          },
        )
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((showSimilarProducts) => {
            if (
              showSimilarProducts &&
              this.isExpanded() &&
              this.selectedProductCardContent() ===
                ProductExpandDetailsContentType.SimilarProducts
            ) {
              return;
            } else if (showSimilarProducts) {
              this.keepOpen.emit();
              this.selectedProductCardContent.set(
                ProductExpandDetailsContentType.SimilarProducts,
              );
              this.isKeepOpen.set(true);
            }
            if (
              showSimilarProducts &&
              this.isExpanded() &&
              this.selectedProductCardContent() ===
                ProductExpandDetailsContentType.Information
            ) {
              this.selectedProductCardContent.set(
                ProductExpandDetailsContentType.SimilarProducts,
              );
              this.productCard()?.showSimilarProducts.set(true);
              return;
            } else if (
              !showSimilarProducts &&
              this.isExpanded() &&
              this.selectedProductCardContent() ===
                ProductExpandDetailsContentType.SimilarProducts
            ) {
              this.selectedProductCardContent.set(
                ProductExpandDetailsContentType.Information,
              );
              this.isKeepOpen.set(false);
              return;
            } else if (showSimilarProducts) {
              this.keepOpen.emit();
              this.isKeepOpen.set(true);
              this.selectedProductCardContent.set(
                ProductExpandDetailsContentType.SimilarProducts,
              );
              this.productCard()?.showSimilarProducts.set(true);
            }
          })
      : undefined;
  }

  public ngAfterViewInit() {
    this.setExpandAnimationVariables();
    this.subscribeToResizeEvent();
  }

  public showSimilarProducts() {
    this.selectedProductCardContent.set(
      ProductExpandDetailsContentType.SimilarProducts,
    );
    this.productCard()?.showSimilarProducts.set(true);
  }

  public showInformationPanel() {
    this.selectedProductCardContent.set(
      ProductExpandDetailsContentType.Information,
    );
    this.productCard()?.showSimilarProducts.set(false);
  }

  public setExpandAnimationVariables() {
    const clientRect = this.hostEl.getBoundingClientRect();
    if (clientRect.right > window.innerWidth - clientRect.width) {
      this.hostEl.style.setProperty(expandWrapperLeftCssProperty, 'auto');
      this.isReverse.set(true);
    } else {
      this.hostEl.style.setProperty(expandWrapperLeftCssProperty, '0');
      this.isReverse.set(false);
    }
    this.hostEl.style.setProperty(
      '--product-card-width-computed',
      clientRect.width + 'px',
    );
  }

  private subscribeToResizeEvent() {
    let clientRect = this.hostEl.getBoundingClientRect();
    fromEvent(window, 'resize')
      .pipe(
        debounceTime(1200),
        tap(() => {
          clientRect = this.hostEl.getBoundingClientRect();
          this.hostEl.style.setProperty(
            '--product-card-width-computed',
            clientRect.width + 'px',
          );
          if (clientRect.right > window.innerWidth - clientRect.width) {
            this.hostEl.style.setProperty(expandWrapperLeftCssProperty, 'auto');
            this.hostEl.classList.add(reverseExpandClassName);
          } else {
            this.hostEl.style.setProperty(expandWrapperLeftCssProperty, '0');
            this.hostEl.classList.remove(reverseExpandClassName);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
